import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, AlertOctagon, AlertTriangle, TrendingDown, Activity } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

function MetricCard({ label, value, icon: Icon, tone = "default", testid, sub }) {
  const toneCls = {
    default: "text-white",
    error: "text-red-400",
    warning: "text-amber-400",
    muted: "text-zinc-400",
  }[tone];
  return (
    <div data-testid={testid} className="border border-zinc-800 bg-[#0A0A0A] p-5 rounded-sm">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      </div>
      <div className={`mt-3 font-mono font-semibold tracking-tight text-3xl md:text-4xl ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] font-mono text-zinc-500 truncate" title={sub}>{sub}</div>}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return iso; }
}

function absTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toISOString().replace("T", " ").slice(0, 19);
  } catch { return iso; }
}

export default function Errors() {
  const [q, setQ] = useState("");
  const [feedId, setFeedId] = useState("");
  const [page, setPage] = useState(0);
  const limit = 100;

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["errors-stats"],
    queryFn: async () => (await api.get("/errors/stats")).data,
    refetchInterval: 20000,
  });

  const { data: feeds = [] } = useQuery({
    queryKey: ["feeds-simple"],
    queryFn: async () => (await api.get("/feeds")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["errors", q, feedId, page],
    queryFn: async () => (await api.get("/errors", { params: { q: q || undefined, feed_id: feedId || undefined, limit, skip: page * limit } })).data,
    keepPreviousData: true,
    refetchInterval: 20000,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const topTypes = stats?.top_error_types || [];
  const topFeeds = stats?.top_failing_feeds || [];
  const maxTypeCount = topTypes[0]?.count || 1;

  return (
    <div className="space-y-6" data-testid="errors-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Diagnostics</div>
        <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Error Log</h1>
        <p className="text-sm text-zinc-400 mt-1">Every failed fetch is registered here. Metrics cover the last 7 days.</p>
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Errors 24h" value={statsLoading ? "…" : (stats?.errors_24h ?? 0)} icon={AlertOctagon} tone="error" testid="metric-errors-24h" />
        <MetricCard label="Errors 7d" value={statsLoading ? "…" : (stats?.errors_7d ?? 0)} icon={TrendingDown} tone="warning" testid="metric-errors-7d" />
        <MetricCard label="Failing Feeds" value={statsLoading ? "…" : (stats?.failing_feeds ?? 0)} icon={AlertTriangle} tone="error" testid="metric-failing-feeds" />
        <MetricCard label="Most Common" value={stats?.most_common_error || "—"} icon={Activity} tone="muted" testid="metric-most-common" sub={topTypes[0] ? `${topTypes[0].count} events` : ""} />
      </div>

      {/* Two-column: top types + top feeds */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Errors by type (7d)</div>
          <div className="p-4 space-y-2" data-testid="top-error-types">
            {topTypes.length === 0 && <div className="text-zinc-500 font-mono text-xs">— no errors registered —</div>}
            {topTypes.map((t) => (
              <div key={t.type} className="space-y-1">
                <div className="flex items-center justify-between text-xs font-mono">
                  <span className="text-zinc-300 truncate">{t.type}</span>
                  <span className="text-zinc-400 tabular-nums">{t.count}</span>
                </div>
                <div className="h-1 bg-zinc-900 rounded-sm overflow-hidden">
                  <div className="h-full bg-red-500/60" style={{ width: `${(t.count / maxTypeCount) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
          <div className="px-5 py-3 border-b border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">Top failing feeds (7d)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-900">
                  <th className="text-left px-4 py-2">Feed</th>
                  <th className="text-right px-4 py-2">Errors</th>
                  <th className="text-right px-4 py-2">Streak</th>
                </tr>
              </thead>
              <tbody data-testid="top-failing-feeds">
                {topFeeds.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-4 text-center text-zinc-500 font-mono text-xs">— none —</td></tr>
                )}
                {topFeeds.map((f) => (
                  <tr key={f.feed_id} className="border-b border-zinc-900 hover:bg-zinc-900/60">
                    <td className="px-4 py-2 font-mono truncate max-w-[240px]" title={f.url}>
                      <div className="text-white truncate">{f.name}</div>
                      <div className="text-[10px] text-red-400 truncate" title={f.last_error}>{f.last_error || "—"}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{f.error_count_7d}</td>
                    <td className="px-4 py-2 text-right">
                      <Badge className="rounded-sm bg-transparent border border-red-500/50 text-red-400 font-mono">{f.consecutive_failures}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Log table */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            data-testid="errors-search-input"
            placeholder="Search error text"
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
            className="pl-9 w-72 bg-transparent border-zinc-800 rounded-sm font-mono text-sm"
          />
        </div>
        <select
          data-testid="errors-feed-filter"
          value={feedId}
          onChange={(e) => { setPage(0); setFeedId(e.target.value); }}
          className="bg-transparent border border-zinc-800 rounded-sm font-mono text-sm px-2 py-2 text-zinc-200 max-w-[260px]"
        >
          <option value="">All feeds</option>
          {feeds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <div className="font-mono text-[11px] text-zinc-500">
          <span data-testid="errors-total">{total}</span> total events
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
              <th className="text-left px-4 py-3">Timestamp (UTC)</th>
              <th className="text-left px-4 py-3">Feed</th>
              <th className="text-left px-4 py-3">HTTP</th>
              <th className="text-left px-4 py-3">Error</th>
              <th className="text-right px-4 py-3">Ago</th>
            </tr>
          </thead>
          <tbody data-testid="errors-tbody">
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500 font-mono text-xs">LOADING…</td></tr>}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-500 font-mono text-xs">— no errors registered —</td></tr>
            )}
            {items.map((e, i) => (
              <tr key={i} className="border-b border-zinc-900 hover:bg-zinc-900/60">
                <td className="px-4 py-2 font-mono text-xs text-zinc-300 whitespace-nowrap">{absTime(e.at)}</td>
                <td className="px-4 py-2 font-mono">
                  <div className="text-white truncate max-w-[220px]">{e.feed_name}</div>
                  <div className="text-[10px] text-zinc-500 truncate max-w-[220px]" title={e.feed_url}>{e.feed_url}</div>
                </td>
                <td className="px-4 py-2 font-mono">
                  {e.http_status ? (
                    <Badge className={`rounded-sm bg-transparent border font-mono ${e.http_status >= 400 ? "border-red-500/50 text-red-400" : "border-zinc-700 text-zinc-300"}`}>
                      {e.http_status}
                    </Badge>
                  ) : <span className="text-zinc-500 text-xs">—</span>}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-red-400 max-w-[420px]">
                  <div className="truncate" title={e.error}>{e.error || "unknown"}</div>
                </td>
                <td className="px-4 py-2 font-mono text-xs text-zinc-400 text-right whitespace-nowrap">{relTime(e.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button data-testid="errors-prev-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
        <div className="font-mono text-xs text-zinc-500">page {page + 1} / {Math.max(1, Math.ceil(total / limit))}</div>
        <Button data-testid="errors-next-btn" variant="outline" className="rounded-sm border-zinc-800 bg-transparent" disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>
    </div>
  );
}
